import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Trash2, ArrowRight, Handshake, Users, Building2, Landmark, Layers } from "lucide-react";

const TYPE_META = {
  supplier: { icon: Handshake, c: "#8B5CF6", key: "form.supplier" },
  union: { icon: Users, c: "#F59E0B", key: "form.union" },
  rent: { icon: Building2, c: "#EAB308", key: "form.rent" },
  tax: { icon: Landmark, c: "#3B82F6", key: "form.tax" },
};
const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0) + " €";
const FILTERS = ["all", "supplier", "union", "rent", "tax"];

export default function History() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sims, setSims] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const params = {};
    if (search) params.search = search;
    if (filter !== "all") params.conflict_type = filter;
    const r = await api.get("/simulations", { params });
    setSims(r.data);
  }, [search, filter]);

  useEffect(() => {
    const tm = setTimeout(load, 250);
    return () => clearTimeout(tm);
  }, [load]);

  const remove = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm(t("history.confirm_delete"))) return;
    await api.delete(`/simulations/${id}`);
    toast.success(t("history.deleted"));
    setSims((s) => s.filter((x) => x.id !== id));
  };

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        <h1 className="font-heading font-bold text-3xl tracking-tight mb-7">{t("history.title")}</h1>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="history-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("history.search_ph")}
              className="bg-[#151A22] border-border focus-visible:ring-primary pl-9" />
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => {
              const active = filter === f;
              const label = f === "all" ? t("history.filter_all") : t(TYPE_META[f].key);
              return (
                <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
                  className={`px-3.5 py-2 rounded-lg text-sm transition-all border ${active ? "bg-primary/15 border-primary/40 text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {sims.length === 0 ? (
          <div className="rounded-xl bg-[#151A22] border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="history-empty">
            {t("history.empty")}
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {sims.map((s) => {
              const m = TYPE_META[s.conflict_type] || {}; const Icon = m.icon || Layers;
              return (
                <div key={s.id} onClick={() => navigate(`/simulation/${s.id}`)} data-testid={`history-item-${s.id}`}
                  className="cursor-pointer rounded-xl bg-[#151A22] border border-border p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#3a4456] transition-all">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m.c}1f`, border: `1px solid ${m.c}55` }}>
                    <Icon className="h-5 w-5" style={{ color: m.c }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.title}</div>
                    <div className="text-xs text-muted-foreground font-mono-fin">{new Date(s.created_at).toLocaleString()}</div>
                  </div>
                  {s.contested_amount ? <div className="font-mono-fin text-sm text-[#EAB308] shrink-0 hidden sm:block">{fmtEur(s.contested_amount)}</div> : null}
                  <button data-testid={`delete-${s.id}`} onClick={(e) => remove(e, s.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1">
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
